import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import moment from 'moment';
import { Moment } from 'moment';
import { TranslocoService } from '@jsverse/transloco';

import { ShuttelbussService } from '../../services/shuttlebuss.service';
import { Flightinfo, TravelTime, TravelTimeMaker } from '../../models/flightModel';
import { MatSelectChange } from '@angular/material/select';

/**
 * @title Display value autocomplete
 */

@Component({
  selector: 'app-booking-select-departure',
  templateUrl: './booking-select-departure.component.html',
  styleUrls: ['./booking-select-departure.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BokoingSelectDepartureComponent implements OnInit {
  @Input() parking = false;
  @Input() startDate: Moment = moment();
  @Output() newItemEvent = new EventEmitter<TravelTime>();


  isLoading = false;
  departureflights: Flightinfo[];
  filteredFlightOptions: Observable<Flightinfo[]>;

  arriveAtParking60Min: string;
  arriveAtParking75Min: string;
  arriveAtParking90Min: string;
  arriveAtParking105Min: string;
  arriveAtParking120Min: string;
  arriveAtParking135Min: string;
  arriveAtParking150Min: string;
  arriveAtParking165Min: string;
  arriveAtParking180Min: string;
  arriveAtParking195Min: string;
  arriveAtParking210Min: string;
  arriveAtParking225Min: string;
  arriveAtParking240Min: string;
  arriveAtParking255Min: string;
  arriveAtParking270Min: string;

  flightDepartureForm = this.fb.group({
    flightDepartureDate: [{ value: '' }, Validators.required],
    flightNumber: [{ value: '', disabled: true }, Validators.required],
    parkingfrom: [{ value: '', disabled: true }],
  });

  private selectedFlight: Flightinfo;

  constructor(
    private fb: UntypedFormBuilder,
    public shuttleBussService: ShuttelbussService,
    public translate: TranslocoService,
  ) { }

  ngOnInit() {
    if (this.parking) {
      this.flightDepartureForm.controls["parkingfrom"].setValidators([Validators.required]);
      this.flightDepartureForm.controls["parkingfrom"].updateValueAndValidity();
    }
  }

  resetForm() {
    this.flightDepartureForm.controls.flightDepartureDate.setValue('');
    this.resetDepartureFlight();
    this.flightDepartureForm.controls.flightNumber.disable();
  }

  displayDepartureFlight(flight: Flightinfo): string {
    return flight && flight.flightId ? flight.flightId + ' ' + flight.arrivalAirportSwedish + ' kl:' + moment(flight.departureTime.scheduled).format('HH:mm') + ' ' + flight.codeShareData : '';
  }

  resetDepartureFlight() {
    this.selectedFlight = null;
    this.newItemEvent.emit(null);
    this.flightDepartureForm.controls.flightNumber.setValue('');
    this.filteredFlightOptions = this.flightDepartureForm.controls.flightNumber.valueChanges.pipe(
      startWith(''),
      map(value => this._filterDepartureFlightsAutocomplete(value))
    );
    this.flightDepartureForm.controls.flightDepartureDate.enable();
    this.flightDepartureForm.controls.flightNumber.enable();
    this.flightDepartureForm.controls.parkingfrom.setValue('');
    this.flightDepartureForm.controls.parkingfrom.disable();
  }

  getDepartureFlightsByDate(date: any) {
    this.flightDepartureForm.controls.flightNumber.setValue('');
    this.isLoading = true;
    this.shuttleBussService.getFlights(date.value.format('YYYY-MM-DD'), 'departure').subscribe(
      flightInfo => {
        this.departureflights = flightInfo
          .filter(
            flight =>
              flight.locationAndStatus.flightLegStatus !== 'DEL' &&
              flight.locationAndStatus.flightLegStatus !== 'Deleted' &&
              flight.departureTime.scheduled > moment().format()
          )
          .sort((a, b) => (a.departureTime.scheduled < b.departureTime.scheduled ? -1 : 1));
        this.isLoading = false;
        this.resetDepartureFlight();
      },
      error => {
        this.isLoading = false;
        console.error(error);
      }
    );
  }

  private _filterDepartureFlightsAutocomplete(value: string): Flightinfo[] {
    const filterValue = value.toLowerCase();
    return this.departureflights.filter(
      option =>
        option.flightId.toLowerCase().includes(filterValue) ||
        option.arrivalAirportSwedish.toLowerCase().includes(filterValue) ||
        option.arrivalAirportEnglish.toLowerCase().includes(filterValue) ||
        option.codeShareData
          .toString()
          .toLowerCase()
          .includes(filterValue)
    );
  }

  onDepartureFlightChange(event) {
    this.flightDepartureForm.controls.flightDepartureDate.disable();
    this.flightDepartureForm.controls.flightNumber.disable();
    this.flightDepartureForm.controls.parkingfrom.setValue('');
    this.flightDepartureForm.controls.parkingfrom.enable();
    this.selectedFlight = event.option.value;

    if (!this.parking) {
      this.newItemEvent.emit(this.getTravelTime(event.option.value, null));
    }
    else {
      this.populateParkingStartTime(event.option.value.departureTime.scheduled);
    }
  }

  onParkingStartChange(event: MatSelectChange) {
    this.selectedFlight.departureTime.actual = moment(this.selectedFlight.departureTime.actual).toString();
    this.selectedFlight.departureTime.scheduled = moment(this.selectedFlight.departureTime.scheduled).toString();
    this.selectedFlight.departureTime.estimated = moment(this.selectedFlight.departureTime.estimated).toString();
    this.newItemEvent.emit(this.getTravelTime(this.selectedFlight, moment(event.value).toDate()));
  }

  private populateParkingStartTime(startTime: string) {
    this.arriveAtParking60Min = moment(startTime)
      .subtract(60, 'm')
      .format();
    this.arriveAtParking75Min = moment(startTime)
      .subtract(75, 'm')
      .format();
    this.arriveAtParking90Min = moment(startTime)
      .subtract(90, 'm')
      .format();
    this.arriveAtParking105Min = moment(startTime)
      .subtract(105, 'm')
      .format();
    this.arriveAtParking120Min = moment(startTime)
      .subtract(120, 'm')
      .format();
    this.arriveAtParking135Min = moment(startTime)
      .subtract(135, 'm')
      .format();
    this.arriveAtParking150Min = moment(startTime)
      .subtract(150, 'm')
      .format();
    this.arriveAtParking165Min = moment(startTime)
      .subtract(165, 'm')
      .format();
    this.arriveAtParking180Min = moment(startTime)
      .subtract(180, 'm')
      .format();
    this.arriveAtParking195Min = moment(startTime)
      .subtract(195, 'm')
      .format();
    this.arriveAtParking210Min = moment(startTime)
      .subtract(210, 'm')
      .format();
    this.arriveAtParking225Min = moment(startTime)
      .subtract(225, 'm')
      .format();
    this.arriveAtParking240Min = moment(startTime)
      .subtract(240, 'm')
      .format();
    this.arriveAtParking255Min = moment(startTime)
      .subtract(255, 'm')
      .format();
    this.arriveAtParking270Min = moment(startTime)
      .subtract(270, 'm')
      .format();
  }

  private getTravelTime(flight: Flightinfo, parkingStart: Date): TravelTime {
    return TravelTimeMaker.create({ flight: flight, parkingStart })
  }

}
